import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getPharmacyQueue } from '../../api/orders.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import QueueTabs, { makeClassifier } from '../../components/QueueTabs.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'

// Prescriptions are "done" when fully dispensed or cancelled. Active
// and draft both belong to the New tab — both demand pharmacist
// attention (active = ready to fulfil; draft = doctor still composing
// but pharmacy can preview).
const classifyPrescription = makeClassifier({
  doneStatuses: ['dispensed', 'cancelled', 'completed'],
})

/**
 * Pharmacy queue — lists prescriptions routed to this pharmacy.
 *
 * Each row shows the drug list (from items[], with legacy medication_name as
 * a fallback) plus a status chip. The detail page handles state transitions
 * and capturing dispense notes.
 */
export default function PharmacyDashboard() {
  const q = useQuery({
    queryKey: ['pharmacy-queue'],
    queryFn: getPharmacyQueue,
    refetchInterval: 15_000,
  })

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Pharmacy queue"
          subtitle="Prescriptions routed to your pharmacy"
          right={
            <div className="flex items-center gap-3">
              <Link to="/pharmacy/catalogue" className="text-xs text-wantok-700 underline">
                My catalogue
              </Link>
              <a href="/pharmacy-sop.pdf" target="_blank" rel="noopener noreferrer" className="text-xs text-wantok-700 underline">Staff SOP</a>
              <button onClick={() => q.refetch()} className="text-xs text-wantok-700 underline">Refresh</button>
            </div>
          }
        />
        {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
        {q.isLoading ? (
          <div className="py-3 text-sm text-slate-400">Loading…</div>
        ) : (
          <QueueTabs
            rows={q.data || []}
            classifyRow={classifyPrescription}
            keyExtractor={(rx) => rx.id}
            tabLabels={{
              new: 'New Prescriptions',
              processed: 'Processed Prescriptions',
              archived: 'Archived Prescriptions',
            }}
            emptyMessages={{
              new: 'No active prescriptions waiting to be dispensed.',
              processed: 'No prescriptions dispensed in the last 30 days.',
              archived: 'No older prescriptions in the archive.',
            }}
            renderRow={(rx) => {
              const drugs = (rx.items || []).map((i) => i.drug_name).filter(Boolean)
              const drugLabel = drugs.length ? drugs.join(', ') : (rx.medication_name || 'Prescription')
              // Patient name is the primary header — dispensing safety
              // starts with the pharmacist confirming the recipient
              // before the drug.
              const patient = rx.patient_name || 'Unknown patient'
              return (
                <li key={rx.id} className="py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{patient}</div>
                    <div className="text-sm text-slate-700 truncate">{drugLabel}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                      <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(rx.status)}`}>{rx.status}</span>
                      <span>Ordered {formatDateTime(rx.created_at)}</span>
                      {rx.patient_phone && <span>· ☎ {rx.patient_phone}</span>}
                      {rx.notes && <span className="truncate">· {rx.notes}</span>}
                    </div>
                  </div>
                  <Link to={`/pharmacy/orders/${rx.id}`} className="text-xs text-wantok-700 underline">Open</Link>
                </li>
              )
            }}
          />
        )}
      </Card>
    </div>
  )
}
